// NOTES
// - currently using projectTimeReceived to force a form update when project data loads :/
import { get } from "lodash";
import React, { PureComponent } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { Container, Header } from "semantic-ui-react";

import RedirectWithReset from "../../components/utils/RedirectWithReset.jsx";
import SimplePreloader from "../../components/preloaders/SimplePreloader.jsx";
import ProjectWizardSteppedForm from "../../components/projectWizard/ProjectWizardSteppedForm.jsx";

import { IMAGES } from "../../state/actions/actionConstants.js";
import { AT_TYPES } from "../../constants/entity.js";

import { getProject } from "../../state/reducers/projects/projectsActions.js";
import { corePartRefreshAction } from "../../state/reducers/core/coreActions.js";
import {
  projectWizardSubmit,
  projectWizardReset
} from "../../state/reducers/projectWizard/projectWizardActions.js";

import { getConfigByConfigId } from "../../state/reducers/projectWizard/projectWizardUtils.js";

import { selectLatestViolationErrors } from "../../state/reducers/api/apiSelectors.js";

import projectSelector from "../../state/reducers/projects/projectSelector.js";
import {
  selectProvider,
  selectRegion
} from "../../state/reducers/projectWizard/projectWizardSelectors.js";

import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class ProjectWizard extends PureComponent {
  static propTypes = {
    isFetching: PropTypes.bool.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    isComplete: PropTypes.bool.isRequired,
    lastCreated: PropTypes.string,
    violationErrors: PropTypes.object,
    project: PropTypes.object,
    match: PropTypes.object.isRequired,

    getProject: PropTypes.func.isRequired,
    getImages: PropTypes.func.isRequired,
    submitWizard: PropTypes.func.isRequired,
    resetWizard: PropTypes.func.isRequired,

    provider: PropTypes.object,
    region: PropTypes.object,
    copy: PropTypes.func.isRequired
  };

  constructor(props) {
    super(props);

    const { configId } = props.match.params;
    const config = getConfigByConfigId(configId);

    this.state = {
      config,
      cancelWizard: false
    };
  }

  componentDidMount() {
    const projectId = this.getProjectId();

    if (projectId) {
      this.props.getProject(projectId, false, false);
    }
    this.props.getImages();
  }

  componentWillUnmount() {
    this.props.resetWizard();
  }

  // -- HANDLERS -- //

  getProjectId = () => {
    const projectId = get(this.props, "match.params.id", null);
    return projectId ? `/projects/${projectId}` : null;
  };

  onCancel = () => {
    this.setState({ cancelWizard: true });
    this.props.resetWizard();
  };

  // triggered whenever the reset button is clicked
  onReset = () => {
    this.props.resetWizard();
  };

  // only triggered whenever the submit button is clicked and the form is valid
  onSubmit = values => {
    // console.log("ProjectWizard onSubmit", values);
    this.props.submitWizard(values, this.getProjectId());
  };

  // -- RENDER -- //

  renderHeader = () => {
    const { config } = this.state;
    const { project, copy } = this.props;

    const projectName = project ? ` - ${project.name}` : "";

    return (
      <Header as="h3">{`${copy("project_wizard.title")}${projectName} - ${copy(
        `${config.copyKey}.title`
      )}`}</Header>
    );
  };

  render() {
    const { config, cancelWizard } = this.state;
    const {
      projectTimeReceived,
      project,
      provider,
      region,
      isFetching,
      isSubmitting,
      isComplete,
      lastCreated,
      violationErrors,
      history,
      match
    } = this.props;

    // redirect to new project if creation was successful
    if (isComplete && lastCreated) {
      return <RedirectWithReset to={lastCreated} />;
    }

    // redirect to the existing project page once submission is complete or cancel is pressed
    if (isComplete || cancelWizard) {
      const projectId = this.getProjectId();
      return <RedirectWithReset to={projectId ? projectId : "/projects"} />;
    }

    return (
      <Container className="main-responsive project-wizard">
        {(isSubmitting || isFetching) && <SimplePreloader />}

        {this.renderHeader()}

        <ProjectWizardSteppedForm
          key={`project-form-${projectTimeReceived}`}
          project={project}
          config={config}
          provider={provider}
          region={region}
          violationErrors={violationErrors}
          match={match}
          history={history}
          onReset={this.onReset}
          onCancel={this.onCancel}
          onSubmit={this.onSubmit}
        />
      </Container>
    );
  }
}

const mapStateToProps = (state, props) => ({
  projectTimeReceived: state.projects.timeReceived,
  isFetching: state.projects.isFetching,
  isSubmitting: state.projectWizard.isSubmitting,
  isComplete: state.projectWizard.isComplete,
  lastCreated: state.projectWizard.lastCreated,
  violationErrors: selectLatestViolationErrors(state),
  provider: selectProvider(state, props),
  region: selectRegion(state, props),
  project: projectSelector.selectProject()(state, props),
  copy: selectCopy(state)
});

const mapDispatchToProps = dispatch => ({
  getProject: (id, usage, resources) =>
    dispatch(getProject(id, usage, resources)),
  getImages: () =>
    dispatch(corePartRefreshAction(IMAGES, AT_TYPES.IMAGE, false)),
  submitWizard: (steps, projectId) =>
    dispatch(projectWizardSubmit(steps, projectId)),
  resetWizard: () => dispatch(projectWizardReset())
});

export default connect(mapStateToProps, mapDispatchToProps)(ProjectWizard);
